import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';

import { MY_FEATURE_WIZARD } from '../../graphql/queries';
import { WIZARD_PARAM } from './constants';
import type { WizardPresentation } from './types';
import { useWizardRecorder } from './useWizardRecorder';
import { WizardOverlay } from './WizardOverlay';

/**
 * One landing: a signed-in viewer on a page carrying `?wizard={slug}` with the flag on. Mounted
 * only once every gate above it has passed — which is what makes "no query is issued" true
 * rather than merely skipped — and keyed by slug, so a second link in the same session is a
 * fresh landing rather than leftover state.
 *
 * It owns the three things the overlay must not: the lookup, the arrival, and the parameter.
 */
export function WizardLanding({ slug }: { slug: string }) {
  const [, setSearchParams] = useSearchParams();
  const [closed, setClosed] = useState(false);
  const recorder = useWizardRecorder(slug);

  // Never cached: within one session the same link must ask the server again, because whether
  // anything opens depends on a row this viewer may have written since.
  const { data } = useQuery<{ myFeatureWizard: WizardPresentation | null }>(MY_FEATURE_WIZARD, {
    variables: { slug },
    fetchPolicy: 'network-only',
  });
  const wizard = data?.myFeatureWizard ?? null;

  /**
   * Through react-router, replacing rather than pushing: the parameter has been consumed, and a
   * back button that restored it would re-open a wizard the reader just finished.
   */
  const strip = useCallback(() => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(WIZARD_PARAM);
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  // An unknown, unpublished or already-finished slug: the page behaves as if the parameter were
  // never there, and the parameter goes, so a reload is not a second futile lookup.
  useEffect(() => {
    if (!data) return;
    if (data.myFeatureWizard) return;
    strip();
  }, [data, strip]);

  /** Only a wizard-audience presentation with steps has anything to show. */
  const opens = wizard?.assignment === 'WIZARD' && wizard.steps.length > 0;

  /**
   * ARRIVED is the row every other event is written against, so both audiences record it and
   * nothing else may precede it. Control is measured by arrivals alone: it strips on success and
   * renders nothing, and an arrival that failed to record keeps its parameter so the next visit
   * re-attempts it rather than losing the audience count.
   *
   * The wizard audience keeps the parameter until it finishes or is dismissed, so a mid-wizard
   * reload re-opens the overlay rather than abandoning it.
   */
  useEffect(() => {
    if (!wizard) return;
    let cancelled = false;
    recorder.record({ eventType: 'ARRIVED' }).then((result) => {
      if (cancelled || !result.ok) return;
      if (!opens) strip();
    });
    return () => {
      cancelled = true;
    };
  }, [wizard, opens, recorder, strip]);

  if (!wizard || !opens) return null;
  if (closed) return null;

  return <WizardOverlay wizard={wizard} recorder={recorder} onClose={() => setClosed(true)} onStrip={strip} />;
}
